import { faEllipsis, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useEffect, useMemo } from 'react';
import { Img } from 'react-image';
import { toast } from 'react-toastify';
import { ParentApi } from '../API/ParentApi';
import UserApi from '../API/UserApi';
import { useAuth } from '../Auth/AuthProvider';
import { Avatar } from '../Components/Avatar';
import DropdownMenu from '../Components/DropdownMenu';
import LoadingIcon from '../Components/LoadingIcon';
import { AnalyticsEvent, useAnalytics } from '../Context/AnalyticsProvider';
import Modal from '../Modal/Modal';
import { useNavigate } from '../ProtectedRoute';
import { User } from '../Types/User';
import CreateStudentView from './CreateStudentView';
import EditStudentView from './EditStudentView';
import NoStudentsView from './NoStudentsView';

interface ParentDashboardViewProps {}
const ParentDashboardView: React.FC<ParentDashboardViewProps> = ({}) => {
  const { impersonate } = useAuth();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const [studentUsers, setStudentUsers] = React.useState<User[]>();

  useEffect(() => {
    trackEvent(AnalyticsEvent.PARENT_DASHBOARD_OPENED);

    // Fetch student users
    const parentApi = new ParentApi();
    parentApi
      .getStudents()
      .then((students) => {
        setStudentUsers(students.sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch((error) => {
        console.error('Failed to fetch student users', error);
        toast.error('Failed to fetch students. Please refresh the page.');
      });
  }, []);

  const isInitialized = useMemo(() => {
    return !!studentUsers;
  }, [studentUsers]);

  if (!isInitialized) {
    return (
      <div
        className="flex-1 flex justify-center items-center"
        style={{ height: 'calc(100dvh - 60px)' }}
      >
        <LoadingIcon />
      </div>
    );
  }

  function handleUserCreated(studentUser: User) {
    setStudentUsers((prev) => [...(prev || []), studentUser]);

    // This sends parent to the student paywall - business requirement
    impersonate(studentUser);
  }

  async function handleSwitchAccountsRequested(user: User) {
    await impersonate(user);
    navigate('/');
  }

  if (!studentUsers.length)
    return <NoStudentsView onCreated={handleUserCreated} />;

  return (
    <div className="flex flex-col h-full w-full">
      <div className="flex-1 overflow-y-auto">
        <div className="px-8 py-4 flex flex-col">
          <h1 className="text-xl font-bold text-left">My Accounts</h1>
          <div
            className="grid grid-cols-8 gap-4 mt-4
            max-sm:grid-cols-1
            sm:max-lg:grid-cols-3
            lg:max-xl:grid-cols-4
            xl:max-2xl:grid-cols-6
          "
          >
            <CreateAccountCard onCreated={handleUserCreated} />
            {studentUsers.map((studentUser) => (
              <StudentAccountCard
                user={studentUser}
                onDeleteSucceeded={(userId) => {
                  setStudentUsers((prev) =>
                    prev.filter((user) => user.id !== userId),
                  );
                  toast.success('Account deleted successfully.');
                }}
                onDeleteFailed={(userId, error) => {
                  console.error('Failed to delete account', {
                    userId,
                    error,
                  });
                  toast.error(
                    "Couldn't delete account. Please try again later.",
                  );
                }}
                onEditSucceeded={(updatedUser) => {
                  setStudentUsers((prev) =>
                    prev.map((user) =>
                      user.id === updatedUser.id ? updatedUser : user,
                    ),
                  );
                  toast.success('Account edited successfully.');
                }}
                onEditFailed={(user, error) => {
                  console.error('Failed to edit account', {
                    userId: user.id,
                    error,
                  });
                  toast.error("Couldn't edit account. Please try again later.");
                }}
                onSwitchRequested={handleSwitchAccountsRequested}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const StudentAccountCard: React.FC<{
  user: User;
  onEditSucceeded?: (user: User) => void;
  onEditFailed?: (user: User, error: Error) => void;
  onDeleteSucceeded?: (userId: string) => void;
  onDeleteFailed?: (userId: string, error: Error) => void;
  onSwitchRequested: (user: User) => void;
}> = ({
  user,
  onEditSucceeded,
  onEditFailed,
  onDeleteSucceeded,
  onDeleteFailed,
  onSwitchRequested,
}) => {
  const { trackEvent } = useAnalytics();
  const [isDeletionInProgress, setIsDeletionInProgress] = React.useState(false);
  const [isEditingStudent, setIsEditingStudent] = React.useState(false);

  function handleUserEditRequested() {
    setIsEditingStudent(true);
    trackEvent(AnalyticsEvent.STUDENT_ACCOUNT_EDIT_REQUESTED, {
      userId: user.id,
      targetUserName: user.name,
      targetUserSurname: user.surname,
    });
  }

  function handleUserDeleteRequested() {
    setIsDeletionInProgress(true);
    trackEvent(AnalyticsEvent.STUDENT_ACCOUNT_DELETE_REQUESTED, {
      userId: user.id,
    });
    const userApi = new UserApi();
    userApi
      .deleteUser(user.id)
      .then(() => {
        trackEvent(AnalyticsEvent.STUDENT_ACCOUNT_DELETE_COMPLETED, {
          userId: user.id,
        });

        onDeleteSucceeded?.(user.id);
      })
      .catch((ex) => {
        console.error('Failed to delete account', {
          userId: user.id,
          ex,
        });
        trackEvent(AnalyticsEvent.STUDENT_ACCOUNT_DELETE_FAILED, {
          userId: user.id,
          error: ex,
        });
        onDeleteFailed?.(user.id, ex);
      })
      .finally(() => {
        setIsDeletionInProgress(false);
      });
  }

  const accountMenuTriggerRef = React.useRef<HTMLDivElement>(null);
  return (
    <>
      <div
        className={`
          flex flex-col w-full justify-center text-sm rounded-xl cursor-pointer bg-white border border-gray-200
          max-lg:mt-0
          sm:grow
        `}
        data-testid="student-card"
      >
        <div
          className={`
            flex relative flex-col w-full h-[140px] justify-around
          `}
        >
          <div className="absolute top-2 right-2">
            {!isDeletionInProgress ? (
              <div ref={accountMenuTriggerRef} className="w-8 h-8 text-right">
                <FontAwesomeIcon
                  icon={faEllipsis}
                  className="w-3 mr-1 text-gray-500"
                />
              </div>
            ) : (
              <div>
                <FontAwesomeIcon
                  icon={faSpinner}
                  className="animate-spin h-5 w-5 mr-1 align-middle"
                />
              </div>
            )}
            <DropdownMenu
              options={[
                {
                  title: 'Go to Account',
                  onClick: async () => {
                    onSwitchRequested?.(user);
                  },
                },
                {
                  title: 'Edit Account',
                  onClick: async () => {
                    handleUserEditRequested();
                  },
                },
                {
                  title: 'Delete Account',
                  onClick: async () => {
                    if (
                      window.confirm(
                        `Are you sure you want to delete account "${user.name} ${user.surname}"?`,
                      )
                    ) {
                      handleUserDeleteRequested();
                    }
                  },
                  className: 'text-red-500',
                },
              ]}
              triggerRef={accountMenuTriggerRef}
              positioningClassName={'right-[0.5rem]'}
            />
          </div>
          <div onClick={() => onSwitchRequested(user)}>
            <div className="flex justify-center w-full my-3">
              <Avatar user={user} size={60} />
            </div>
            <div className="flex flex-col items-center px-4 pb-3 mt-auto">
              <div
                title={`${user.name} ${user.surname}`}
                className={`relative mb-auto text-center text-base font-bold max-md:mr-2.5 overflow-hidden text-ellipsis whitespace-nowrap w-[80%]`}
              >
                {user.name} {user.surname}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Modal isOpen={isEditingStudent}>
        <EditStudentView
          user={user}
          onCancel={() => {
            trackEvent(AnalyticsEvent.STUDENT_ACCOUNT_EDIT_FAILED, {
              userId: user.id,
              error: 'Cancelled by user',
            });
            setIsEditingStudent(false);
          }}
          onEdited={(updatedUser) => {
            setIsEditingStudent(false);
            // Update the user in the parent's list
            if (onEditSucceeded && onEditFailed) {
              onEditSucceeded(updatedUser);
            }
          }}
          onFailed={(error) => {
            setIsEditingStudent(false);
            onEditFailed?.(user, error);
          }}
        />
      </Modal>
    </>
  );
};

interface CreateAccountCardProps {
  onCreated: (studentUser: User) => void;
}
const CreateAccountCard: React.FC<CreateAccountCardProps> = ({ onCreated }) => {
  const [isCreatingAccount, setIsCreatingAccount] = React.useState(false);

  return (
    <>
      <div
        className={`
        flex flex-col w-full justify-center text-sm rounded-xl max-md:mt-3 cursor-pointer bg-blue-100 border border-gray-300
        max-lg:mt-0
        sm:grow
      `}
        onClick={() => {
          setIsCreatingAccount(true);
        }}
        data-testid="open-add-student-modal-card"
      >
        <div
          className={`
          flex relative flex-col w-full h-[140px] items-center
        `}
        >
          <Img
            src={[require('../assets/icons/plus.svg')]}
            alt={'Add an Account'}
            className={`object-cover inset-0 rounded-xl m-auto w-[32px]`}
          />
          <div className="flex flex-col items-center px-4 pb-3">
            <div
              className={`relative mb-auto text-center text-base font-bold max-md:mr-2.5`}
            >
              Add an Account
            </div>
            <div
              className={`relative mt-1 text-center opacity-50 max-md:mr-2.5 line-clamp-2`}
            >
              Create a new account for your kid
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={isCreatingAccount}
        children={
          <CreateStudentView
            onCreated={(user) => {
              onCreated(user);
              setIsCreatingAccount(false);
            }}
            onCancel={() => {
              setIsCreatingAccount(false);
            }}
          />
        }
        maxWidthClass={'max-w-[500px]'}
        onClose={() => setIsCreatingAccount(false)}
      />
    </>
  );
};

export default ParentDashboardView;
